import { ChevronRightIcon } from '@prophecy/icons';
import { stopPropagation } from '@prophecy/utils/dom';
import { useFocusHandleOnClose, useFocusTrap } from '@prophecy/utils/react/focus';
import { useControlledState } from '@prophecy/utils/react/hooks';
import {
  Arrow,
  CheckboxItem,
  Group,
  ItemIndicator,
  Portal,
  RadioGroup,
  RadioItem,
  Sub,
  Root
} from '@radix-ui/react-dropdown-menu';
import React, { createContext, useContext, useRef } from 'react';

import { hoverContainProps } from '../constants';
import { Stack, StackItem } from '../Layout';
import {
  Content,
  DropdownFooterContainer,
  Item,
  Label,
  OverlayContent,
  Separator,
  StyledTrigger,
  SubContent,
  SubTrigger
} from './styled';
import { tokens } from './tokens';
import { ItemDataTestIdProps, DropdownProps, DropdownSubMenuProps, Size } from './types';

const ARROW_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);
function stopArrowKeyPropagation(event: React.KeyboardEvent) {
  if (ARROW_KEYS.has(event.key)) event.stopPropagation();
}

export const TriggerContext = createContext<boolean>(false);
export function useTriggerContext() {
  return useContext(TriggerContext);
}
export function Dropdown({
  defaultOpen,
  arrow,
  disabled,
  overlay,
  visible,
  footer,
  onVisibleChange,
  children,
  overlayClassName,
  overlayStyle,
  placement: side = 'bottom',
  placementOffset: sideOffset = 8,
  align = 'start',
  alignOffset,
  portalled = true,
  size = Size.md,
  onCloseAutoFocus,
  onOpenAutoFocus,
  onInteractOutside,
  openOnHover = false,
  modal = true,
  'data-test-id': dataTestId
}: DropdownProps) {
  const [_visible, toggleVisible] = useControlledState({
    value: visible,
    onChange: onVisibleChange
  });

  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleMouseLeave = () => {
    if (!openOnHover) return;
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      toggleVisible(false);
    }, 300);
  };

  const handleMouseEnter = () => {
    if (!openOnHover) return;
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    toggleVisible(true);
  };

  const container = useFocusTrap(Boolean(_visible));

  const defaultOnCloseAutoFocus = useFocusHandleOnClose(Boolean(_visible));

  const content = (
    <Content
      {...hoverContainProps}
      className={overlayClassName}
      style={overlayStyle}
      side={side}
      size={size}
      align={align}
      sideOffset={sideOffset}
      asChild
      alignOffset={alignOffset}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={container}
      tabIndex={-1}
      // @ts-ignore: onOpenAutoFocus is internally used but not exposed on types from radix ui
      onOpenAutoFocus={onOpenAutoFocus}
      onClick={stopPropagation}
      // Radix only preventDefaults arrow keys, so stop them bubbling past the menu.
      onKeyDown={stopArrowKeyPropagation}
      onCloseAutoFocus={onCloseAutoFocus ?? defaultOnCloseAutoFocus}
      onInteractOutside={onInteractOutside}
      data-test-id='dropdown-content'>
      <div>
        {arrow && <Arrow />}
        {overlay && <OverlayContent size={size}>{overlay}</OverlayContent>}
        {footer && <DropdownFooterContainer>{footer}</DropdownFooterContainer>}
      </div>
    </Content>
  );

  return (
    <Root open={_visible} dir='ltr' modal={modal} onOpenChange={toggleVisible} defaultOpen={defaultOpen}>
      <TriggerContext.Provider value={true}>
        <StyledTrigger
          disabled={disabled}
          asChild
          $openOnHover={openOnHover}
          data-test-id={dataTestId}
          onMouseEnter={openOnHover ? handleMouseEnter : undefined}
          onMouseLeave={openOnHover ? handleMouseLeave : undefined}>
          {children}
        </StyledTrigger>
      </TriggerContext.Provider>
      {portalled ? <Portal>{content}</Portal> : content}
    </Root>
  );
}

export function isDropdownMenuOpened() {
  return Boolean(document.querySelector('[role="menu"]'));
}

function SubMenu({
  children,
  overlay,
  triggerArrow = true,
  overlayArrow,
  placementOffset: sideOffset = 8,
  alignOffset,
  open,
  defaultOpen,
  onOpenChange,
  size = Size.md,
  ...rest
}: DropdownSubMenuProps) {
  return (
    <Sub open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <SubTrigger {...rest} size={size}>
        <Stack direction='horizontal' width='100%' gap={tokens.SubMenu.Arrow.gap} alignY='center' align='space-between'>
          <StackItem>{children}</StackItem>
          <StackItem shrink='0'>{triggerArrow && <ChevronRightIcon type='default' size='xs' />}</StackItem>
        </Stack>
      </SubTrigger>
      <SubContent
        data-test-id='dropdown-sub-menu-content'
        size={size}
        sideOffset={sideOffset}
        alignOffset={alignOffset}>
        {overlay && <OverlayContent size={size}>{overlay}</OverlayContent>}
        {overlayArrow && <Arrow />}
      </SubContent>
    </Sub>
  );
}

// to disable onClick when disabled is true
const DropdownItem = React.forwardRef<
  React.ElementRef<typeof Item>,
  React.ComponentProps<typeof Item> & ItemDataTestIdProps
>(({ disabled, onClick, children, ...props }, ref) => {
  const handleClick = disabled ? undefined : onClick;
  return (
    <Item {...props} ref={ref} onClick={handleClick} disabled={disabled}>
      {children}
    </Item>
  );
});

DropdownItem.displayName = 'DropdownItem';

Dropdown.Label = Label;
Dropdown.Item = DropdownItem;
Dropdown.Group = Group;
Dropdown.CheckboxItem = CheckboxItem;
Dropdown.RadioGroup = RadioGroup;
Dropdown.RadioItem = RadioItem;
Dropdown.ItemIndicator = ItemIndicator;
Dropdown.Separator = Separator;
Dropdown.SubMenu = SubMenu;
export { DropdownFooterContainer } from './styled';
export * from './types';
